import React, { useState } from 'react';
import { Container, Tabs, Tab, Card } from 'react-bootstrap';
import { UserManager } from '@components/admin/UserManager';
import { UserProgressView } from '@components/admin/UserProgressView';
import { CourseEnrollmentManager } from '@components/admin/CourseEnrollmentManager';
import { useCourses } from '@hooks';
import { useAuth } from '@context';
import { getManagedCourses, isAdmin } from '@utils/permissions';

/**
 * Users Admin Page
 * Main page for user management with multiple views
 */
export function UsersPage() {
  const { user } = useAuth();
  const [activeView, setActiveView] = useState('users'); // users, progress, enrollments
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const { courses } = useCourses();
  const userIsAdmin = isAdmin(user);
  const scopedCourses = getManagedCourses(user, courses);

  /**
   * Handle viewing user progress
   */
  const handleViewProgress = (user) => {
    setSelectedUser(user);
    setActiveView('progress');
  };

  /**
   * Handle viewing course enrollments
   */
  const handleViewEnrollments = (course) => {
    setSelectedCourse(course);
    setActiveView('enrollments');
  };

  /**
   * Handle back to users list
   */
  const handleBackToUsers = () => {
    setActiveView('users');
    setSelectedUser(null);
    setSelectedCourse(null);
  };

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">User Management</h2>

      {activeView === 'users' && (
        <Tabs defaultActiveKey={userIsAdmin ? 'users' : 'course-enrollments'} className="mb-3">
          {userIsAdmin && (
            <Tab eventKey="users" title="All Users">
              <UserManager onViewProgress={handleViewProgress} />
            </Tab>
          )}
          <Tab eventKey="course-enrollments" title="Course Enrollments">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Manage Course Enrollments</h5>
              </Card.Header>
              <Card.Body>
                <p className="mb-3">Select a course to manage its enrollments:</p>
                <div className="d-grid gap-2">
                  {!scopedCourses || scopedCourses.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No assigned courses available.
                    </div>
                  ) : (
                    scopedCourses.map(course => (
                      <Card 
                        key={course.id} 
                        className="cursor-pointer"
                        onClick={() => handleViewEnrollments(course)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Card.Body className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">{course.title}</h6>
                            <small className="text-muted">{course.description}</small>
                          </div>
                          <span className="text-primary">Manage →</span>
                        </Card.Body>
                      </Card>
                    ))
                  )}
                </div>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      )}

      {userIsAdmin && activeView === 'progress' && selectedUser && (
        <div>
          <div className="mb-3">
            <button className="btn btn-link" onClick={handleBackToUsers}>
              ← Back to Users
            </button>
          </div>
          
          {selectedUser.enrolledCourses.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-5">
                <p className="text-muted">
                  {selectedUser.name} is not enrolled in any courses yet.
                </p>
              </Card.Body>
            </Card>
          ) : (
            <Tabs defaultActiveKey={selectedUser.enrolledCourses[0]} className="mb-3">
              {selectedUser.enrolledCourses.map(courseId => {
                const course = courses?.find(c => c.id === courseId);
                if (!course) return null;
                
                return (
                  <Tab 
                    eventKey={courseId} 
                    title={course.title} 
                    key={courseId}
                  >
                    <UserProgressView
                      user={selectedUser}
                      courseId={courseId}
                      courseName={course.title}
                    />
                  </Tab>
                );
              })}
            </Tabs>
          )}
        </div>
      )}

      {activeView === 'enrollments' && selectedCourse && (
        <CourseEnrollmentManager
          courseId={selectedCourse.id}
          courseName={selectedCourse.title}
          onBack={handleBackToUsers}
        />
      )}
    </Container>
  );
}
